// 消息处理工具

import { v4 as uuidv4 } from 'uuid';
import { Message, ServerMessage } from '../types';

// 消息工具类
import { cached } from './cache';

export class MessageUtils {
  // 将服务器消息转换为本地消息格式
  static convertServerMessage(serverMsg: ServerMessage, isNew: boolean = false): Message {
    return {
      id: `server_${serverMsg.id}`,
      type: serverMsg.to_client === 'user' ? 'bot' : 'user',
      content: serverMsg.content,
      timestamp: new Date(serverMsg.add_timestamp * 1000),
      status: 'sent' as const,
      messageKey: serverMsg.message_key,
      serverId: serverMsg.id,
      readFlag: serverMsg.read_flag,
      isNew
    };
  }

  // 创建新消息
  static createMessage(
    content: string, 
    type: 'user' | 'bot', 
    status: 'sending' | 'sent' | 'failed' = 'sent'
  ): Message {
    return {
      id: uuidv4(),
      type,
      content,
      timestamp: new Date(),
      status,
      retryCount: 0,
      isNew: true // 本地添加的消息都标记为新消息
    };
  }

  // 消息排序函数 - 按时间戳升序排列（优化版本）
  static sortMessagesByTimestamp(messages: Message[]): Message[] {
    // 如果消息数量较少，直接排序
    if (messages.length <= 50) {
      return [...messages].sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
    }
    
    // 对于大量消息，使用更高效的排序策略
    // 先按时间戳分组，然后合并排序
    const timeGroups = new Map<number, Message[]>();
    
    messages.forEach(msg => {
      const timeKey = Math.floor(msg.timestamp.getTime() / 1000); // 按秒分组
      if (!timeGroups.has(timeKey)) {
        timeGroups.set(timeKey, []);
      }
      timeGroups.get(timeKey)!.push(msg);
    });
    
    // 按时间键排序并合并
    const sortedKeys = Array.from(timeGroups.keys()).sort((a, b) => a - b);
    const result: Message[] = [];
    
    sortedKeys.forEach(key => {
      const groupMessages = timeGroups.get(key)!;
      // 组内按毫秒排序
      groupMessages.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
      result.push(...groupMessages);
    });
    
    return result;
  }

  // 过滤新消息（用于轮询）- 简化版本：只用 message_key 去重
  static filterNewMessages(
    serverMessages: ServerMessage[], 
    existingMessages: Message[]
  ): ServerMessage[] {
    // 只使用 message_key 去重（最可靠的唯一标识）
    const existingMessageKeys = new Set(
      existingMessages
        .map(msg => msg.messageKey)
        .filter(Boolean)
    );
    
    // 过滤出真正的新消息
    return serverMessages.filter(serverMsg => {
      // 如果有 message_key，只用它来判断是否重复
      if (serverMsg.message_key) {
        return !existingMessageKeys.has(serverMsg.message_key);
      }
      
      // 如果没有 message_key（异常情况），允许通过
      return true;
    });
  }

  // 更新消息状态
  static updateMessageStatus(
    messages: Message[], 
    messageId: string, 
    status: 'sending' | 'sent' | 'failed',
    retryCount?: number
  ): Message[] {
    return messages.map(msg => 
      msg.id === messageId 
        ? { 
            ...msg, 
            status: status, 
            ...(retryCount !== undefined && { retryCount }) 
          } 
        : msg
    );
  }

  // 标记消息为已读（同时清除新消息动画标记）
  static markMessageAsRead(messages: Message[], messageKey: string): Message[] {
    return messages.map(msg =>
      msg.messageKey === messageKey ? { ...msg, readFlag: 1, isNew: false } : msg
    );
  }

  // 清除新消息标记（优化版：避免不必要的对象创建）
  static clearNewMessageFlags(messages: Message[]): Message[] {
    // 快速检查：如果没有新消息，直接返回原数组
    const hasNewMessages = messages.some(m => m.isNew === true);
    if (!hasNewMessages) return messages;
    
    // 只更新有 isNew 标记的消息
    return messages.map(msg => msg.isNew ? { ...msg, isNew: false } : msg);
  }

  // 限制仅对最新 N 条消息保留新消息动画（优化版：避免不必要的对象创建）
  static limitNewAnimations(messages: Message[], maxNew: number = 10): Message[] {
    // 快速检查：如果没有新消息，直接返回原数组
    const hasNewMessages = messages.some(m => m.isNew === true);
    if (!hasNewMessages) return messages;
    
    if (maxNew <= 0) {
      // 只有在确实有新消息时才创建新对象
      return messages.map(m => m.isNew ? { ...m, isNew: false } : m);
    }
    
    let newCount = 0;
    let needsUpdate = false;
    
    // 从末尾往前统计新消息
    const result = [...messages].reverse().map(m => {
      if (m.isNew) {
        if (newCount < maxNew) {
          newCount += 1;
          return m; // 保留
        }
        needsUpdate = true;
        return { ...m, isNew: false };
      }
      return m;
    }).reverse();
    
    // 如果没有任何变化，返回原数组
    return needsUpdate ? result : messages;
  }

  // 限制消息数量，防止内存泄漏
  static limitMessageCount(messages: Message[], maxMessages: number = 1000): Message[] {
    if (messages.length <= maxMessages) return messages;
    
    // 保留最新的消息，删除最旧的消息
    const sortedMessages = this.sortMessagesByTimestamp(messages);
    const limitedMessages = sortedMessages.slice(-maxMessages);
    
    console.log(`消息数量限制: 从 ${messages.length} 条减少到 ${limitedMessages.length} 条`);
    return limitedMessages;
  }

  // 清理过期的消息（超过指定天数的消息）
  static cleanupOldMessages(messages: Message[], maxAgeDays: number = 7): Message[] {
    const cutoffTime = new Date();
    cutoffTime.setDate(cutoffTime.getDate() - maxAgeDays);
    
    const filteredMessages = messages.filter(msg => msg.timestamp > cutoffTime);
    
    if (filteredMessages.length < messages.length) {
      console.log(`清理过期消息: 从 ${messages.length} 条减少到 ${filteredMessages.length} 条`);
    }
    
    return filteredMessages;
  }

  // 检测消息中的媒体内容 - 使用缓存优化
  static detectMediaContent = cached((content: string): {
    hasImages: boolean;
    hasVideos: boolean;
    hasLinks: boolean;
    imageUrls: string[];
    videoUrls: string[];
    linkUrls: string[];
  } => {
    const imageRegex = /(https?:\/\/[^\s]+\.(jpg|jpeg|png|gif|webp|svg)(\?[^\s]*)?)/gi;
    const videoRegex = /(https?:\/\/[^\s]+\.(mp4|webm|ogg|avi|mov)(\?[^\s]*)?)/gi;
    const linkRegex = /(https?:\/\/[^\s]+)/gi;

    const imageUrls = content.match(imageRegex) || [];
    const videoUrls = content.match(videoRegex) || [];
    const linkUrls = content.match(linkRegex) || [];

    return {
      hasImages: imageUrls.length > 0,
      hasVideos: videoUrls.length > 0,
      hasLinks: linkUrls.length > 0,
      imageUrls,
      videoUrls,
      linkUrls
    };
  }, (content: string) => `media_${content.length}_${content.slice(0, 50)}`);

  // 提取纯文本内容（移除URL）- 使用缓存优化
  static extractTextContent = cached((content: string): string => {
    const urlRegex = /(https?:\/\/[^\s]+)/gi;
    return content.replace(urlRegex, '').trim();
  }, (content: string) => `text_${content.length}_${content.slice(0, 50)}`);

  // 格式化时间显示
  static formatMessageTime(timestamp: Date): string {
    return timestamp.toLocaleTimeString([], {
      year: 'numeric', 
      month: '2-digit', 
      day: '2-digit', 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  }

  // 分割消息内容为多行 - 使用缓存优化
  static splitMessageContent = cached((content: string): string[] => {
    return content.split('\n');
  }, (content: string) => `split_${content.length}_${content.slice(0, 50)}`);

  // 获取最后消息时间戳
  static getLastMessageTimestamp(messages: Message[]): number {
    if (messages.length === 0) return 0;
    return Math.max(...messages.map(msg => msg.timestamp.getTime() / 1000));
  }
}